/**
 * @fileoverview Shared import-template wizard used by system-web and tenant-web.
 * @module import-editor/unifiedWizard
 *
 * Responsibilities:
 * - Orchestrate Gate 1, Gate 2, and Gate 3 authoring flows
 * - Persist draft versus published template state through host-injected services
 * - Host the shared import run flow for the `new-import` intent
 *
 * Dependencies:
 * - `../templateEditor/types` for editor state serialization and hydration
 * - `../hooks/useTemplateEditorState` for local editor state management
 * - `../runFlow/UnifiedImportRunFlow` for the run-intent pipeline
 * - Host `ImportWizardServices` adapters for local-first data access and mutation
 *
 * Architecture:
 * - Hosts provide service adapters; this shared wizard owns flow/state orchestration
 * - Reads enter through host services that must remain Dexie-backed/local-first
 * - Gate 3 publishes a template first, then hands off to the host run flow
 *
 * Notes:
 * - Once a new template receives an `_id`, later saves must update that same record
 * - Gate 3 tenant context is stored only in draft-resume metadata and must never become durable published ownership on the template
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import type {
  ImportTemplate,
  ImportTemplateSampleBlob,
  ImportTemplateSamplePreview,
} from '@firesquirrel/local-db';
import type {
  TemplateEditorState,
  TemplateEditorTab,
  EditorTabDescriptor,
  TemplateEditorMode,
} from '../templateEditor/types';
import {
  templateToState,
  stateToTemplate,
  resolveDraftTemplateName,
} from '../templateEditor/types';
import { getTabBadge, type TabBadge } from '../utils/templateValidation';
import { useTemplateEditorState } from '../hooks/useTemplateEditorState';
import EditorTabBar from '../components/EditorTabBar';
import EditorFooter from '../components/EditorFooter';
import BasicsTabContent from '../components/BasicsTabContent';
import SampleDataTabContent from '../components/SampleDataTabContent';
import RunWizardColumnMappingPanel from '../components/RunWizardColumnMappingPanel';
import MatchKeyTabContent from '../components/MatchKeyTabContent';
import GeocodingTabContent from '../components/GeocodingTabContent';
import ReviewTabContent from '../components/ReviewTabContent';
import ConfirmDiscardModal from '../components/ConfirmDiscardModal';
import TemplateModeGate from '../components/TemplateModeGate';
import ImportEntryGate, {
  type ImportEntryTenantOption,
} from '../components/ImportEntryGate';
import UnifiedImportRunFlow, { type UnifiedImportRunFlowProps } from '../runFlow/UnifiedImportRunFlow';
import type { ImportWizardServices, UnifiedWizardIntent } from './services';

/**
 * Run-flow configuration for the `new-import` intent. Everything the shared
 * UnifiedImportRunFlow needs beyond the wizard's own `services`,
 * `templateId`, `initialFile`, and `onCancel` props.
 */
export type UnifiedWizardRunFlowOptions = Omit<
  UnifiedImportRunFlowProps,
  'services' | 'initialTemplateId' | 'initialFile' | 'onCancel'
>;

export interface UnifiedImportWizardProps {
  /** Service adapter injected by the host app. */
  services: ImportWizardServices;
  /** Which journey the user is on. See services.ts / UnifiedWizardIntent. */
  intent: UnifiedWizardIntent;
  /** Existing template id when editing or when running an import against a
      specific template. Ignored for `create-template`. */
  templateId?: string;
  /** Optional pre-selected file when kicking off an import from a file-drop
      context (e.g. the parcels page). Only consulted for `new-import`. */
  initialFile?: File | null;
  /** Scope toggle for the underlying template-editor hook. System-web
      passes 'system'; tenant-web omits or passes 'department'. */
  scope?: 'department' | 'system';
  /** Called whenever the state should be persisted as a draft (debounced
      inside the hook). The host decides where the draft goes. */
  onAutosaveDraft?: (state: TemplateEditorState) => void;
  /** Invoked after a successful save. Host navigates back to the list. */
  onDone: () => void;
  /** Invoked when the user cancels or backs out. */
  onCancel: () => void;
  /**
   * Optional tenant parked for a "save template & import" follow-on
   * (Card 3 of ImportEntryGate). When set, the Review step's primary
   * publish button becomes "Publish & import to <tenantName>" and
   * on success the wizard calls `onSaveAndRun` instead of `onDone`.
   * The tenant is NOT written to the template — it is only used by
   * the host to kick off the run wizard with the new template.
   */
  runAfterSave?: {
    tenantId: string;
    tenantName?: string;
  };
  /**
   * Companion to `runAfterSave`. Fired instead of `onDone` when both
   * are set and the save succeeds. Host typically navigates into the
   * run wizard with the new template + parked tenant pre-wired.
   *
   * `sampleFile` is the File the user uploaded during bound-mode
   * authoring (may be undefined if they didn't upload one). Hosts
   * should forward it to the run flow via navigation state so the
   * user is NOT asked to re-upload the same file.
   */
  onSaveAndRun?: (
    savedTemplateId: string,
    tenantId: string,
    sampleFile?: File,
    sampleBlob?: ImportTemplateSampleBlob,
    samplePreview?: ImportTemplateSamplePreview,
  ) => void;
  /**
   * Skip the 3-card entry gate and land directly in the editor with
   * this mode pre-selected. Used for deep-links (`?mode=bound`) and
   * for restoring state on refresh when the gate has already been
   * crossed once. Ignored for `edit-template` intent (which always
   * skips the gate by definition).
   */
  initialMode?: 'shell' | 'bound';
  /**
   * Run-flow configuration for the `new-import` intent. The shared
   * UnifiedImportRunFlow owns the run pipeline (upload → parse → map →
   * match-key → dry-run → commit); hosts pass feature flags, render slots,
   * chrome options, and navigation callbacks here. The wizard supplies
   * `services`, `templateId`, `initialFile`, and `onCancel` from its own
   * props.
   */
  runFlow?: UnifiedWizardRunFlowOptions;
  /**
   * Host-supplied tabs appended to the built-in set. System-web uses
   * this to attach a 'visibility' tab; tenant-web leaves it empty.
   *
   * The ids must be unique and must not collide with built-in ids
   * ('basics', 'mappings', 'matchKey', 'geocoding', 'review').
   */
  extraTabs?: EditorTabDescriptor[];
  /** Where to splice `extraTabs` relative to the built-in list.
      Defaults to 'review' — i.e. extras appear after Review + Save,
      which matches the existing system-web ordering. */
  insertExtraTabsAfter?: TemplateEditorTab;
  /**
   * Renders the body for a host-supplied tab. Called only when the
   * current activeTab is one of `extraTabs`. The renderer receives
   * the live state and the same updateState helper used by built-in
   * tabs so extras participate fully in dirty-tracking and autosave.
   */
  renderExtraTab?: (
    tabId: string,
    ctx: {
      state: TemplateEditorState;
      updateState: (patch: Partial<TemplateEditorState>) => void;
    },
  ) => ReactNode;
  /** Optional per-tab error producer for extras. Returning a non-empty
      array lights up the error badge on the tab. */
  extraTabErrors?: (tabId: string, state: TemplateEditorState) => string[];
  /**
   * Config for the initial 3-card ImportEntryGate shown on `create-
   * template` intent when no mode has been picked yet. When omitted,
   * the wizard falls back to the legacy 2-card TemplateModeGate so
   * that callers who haven't migrated keep their current behavior.
   *
   * Host responsibilities:
   *   - System-web passes `tenantOptions` so Card 3 shows a picker and
   *     calls `onChooseImport(tenantId)` with the user's selection.
   *   - Tenant-web passes `implicitTenantId` (and optionally
   *     `implicitTenantName`) so Card 3 hides the picker and submits
   *     that id directly.
   *   - A host that never wants Card 3 (pure template-authoring) can
   *     pass `showImportCard: false`.
   */
  importEntryGate?: {
    tenantOptions?: ImportEntryTenantOption[];
    tenantsLoading?: boolean;
    implicitTenantId?: string;
    implicitTenantName?: string;
    showImportCard?: boolean;
    onChooseTemplateMode?: (mode: 'shell' | 'bound') => void;
    onChooseImport?: (tenantId: string) => void;
  };
}

/**
 * @process ResolveEntryGateSelection
 *
 * Steps:
 * 1. Translate the selected gate into the wizard mode the editor should enter
 * 2. Persist the gate used so draft resume and publish flows retain lineage
 * 3. Carry parked Gate 3 tenant context only when an immediate import follow-on exists
 *
 * Inputs:
 * - `gate`: gate selected by the user
 * - `runTenantId`: optional parked tenant for Gate 3
 * - `runTenantName`: optional parked tenant display name for Gate 3
 *
 * Outputs:
 * - Partial editor-state patch describing the selected gate
 *
 * Failure Handling:
 * - No throw path; Gate 1/2 simply clear any run-tenant context
 *
 * Idempotency:
 * - REQUIRED
 */
export function resolveEntryGateSelection(
  gate: 'gate-1' | 'gate-2' | 'gate-3',
  runTenantId?: string,
  runTenantName?: string,
): Pick<TemplateEditorState, 'mode' | 'entryGate' | 'runTenantId' | 'runTenantName'> {
  const mode: TemplateEditorMode = gate === 'gate-1' ? 'shell' : 'bound';
  return {
    mode,
    entryGate: gate,
    runTenantId: gate === 'gate-3' ? runTenantId : undefined,
    runTenantName: gate === 'gate-3' ? runTenantName : undefined,
  };
}

/**
 * @process ResolveTemplateSaveTarget
 *
 * Steps:
 * 1. Prefer the route template id for edit flows
 * 2. For new flows, reuse the autosaved id once the draft has been created
 * 3. Fall back to create only when no persisted id exists yet
 *
 * Inputs:
 * - `isNew`: whether the route is the create flow
 * - `routeTemplateId`: template id from the route when editing
 * - `autosaveTemplateId`: created id captured after the first draft save
 *
 * Outputs:
 * - `{ mode, templateId }` describing whether the next write should create or update
 *
 * Failure Handling:
 * - No throw path; missing ids resolve to `create`
 *
 * Idempotency:
 * - REQUIRED
 */
export function resolveTemplateSaveTarget(
  isNew: boolean,
  routeTemplateId?: string,
  autosaveTemplateId?: string | null,
): { mode: 'create' | 'update'; templateId?: string } {
  if (routeTemplateId) {
    return { mode: 'update', templateId: routeTemplateId };
  }
  if (isNew && autosaveTemplateId) {
    return { mode: 'update', templateId: autosaveTemplateId };
  }
  return { mode: 'create' };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function UnifiedImportWizard(props: UnifiedImportWizardProps) {
  const { intent, services, templateId, initialFile, onCancel, runFlow } = props;

  if (intent === 'create-template' || intent === 'edit-template') {
    return <AuthoringFlow {...props} intent={intent} />;
  }

  // Run intent — the shared run flow owns the pipeline. Host behavior is
  // injected through the services adapter and the `runFlow` options bag;
  // there is no host slot anymore (Phase 2A, G-005).
  return (
    <UnifiedImportRunFlow
      services={services}
      initialTemplateId={templateId}
      initialFile={initialFile}
      onCancel={onCancel}
      {...runFlow}
    />
  );
}

// ---------------------------------------------------------------------------
// AuthoringFlow — create-template / edit-template
// ---------------------------------------------------------------------------

interface AuthoringFlowProps extends UnifiedImportWizardProps {
  intent: 'create-template' | 'edit-template';
}

function AuthoringFlow({
  services,
  intent,
  templateId,
  scope = 'department',
  onAutosaveDraft,
  onDone,
  onCancel,
  extraTabs,
  insertExtraTabsAfter = 'review',
  renderExtraTab,
  extraTabErrors,
  importEntryGate,
  runAfterSave,
  onSaveAndRun,
  initialMode,
}: AuthoringFlowProps) {
  const isNew = intent === 'create-template';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const panelScrollRef = useRef<HTMLDivElement | null>(null);

  // Cached parse output to power the FieldMappings auto-suggest banner.
  const [availableColumns, setAvailableColumns] = useState<string[] | undefined>(
    undefined,
  );
  const [sampleRows, setSampleRows] = useState<Record<string, unknown>[]>([]);

  // Captured sample File — retained so the Card 3 save-and-import flow
  // can forward it to the run wizard via navigation state, sparing the
  // user a second upload of the same file. Kept as a ref because it
  // does not drive rendering and we don't want stale closures in the
  // save callback.
  const sampleFileRef = useRef<File | null>(null);

  // Canonical server id for autosave. Null until first create() lands.
  const autosaveIdRef = useRef<string | null>(templateId ?? null);

  const {
    state,
    setState,
    updateState,
    goToTab,
    goNext,
    goBack,
    currentTabIndex,
    isLastTab,
    tabs: visibleTabs,
    tabErrors,
    validateForSave,
  } = useTemplateEditorState({
    scope,
    extraTabs,
    insertExtraTabsAfter,
    // Skip the gate when the host has deep-linked straight into a
    // mode (e.g. Card 3 navigation with ?mode=bound&runTenantId=...).
    initialMode,
    onAutosave: useCallback(
      (s: TemplateEditorState) => {
        const id = s._id ?? autosaveIdRef.current;
        if (!id || !onAutosaveDraft) return;
        onAutosaveDraft({ ...s, _id: id });
      },
      [onAutosaveDraft],
    ),
  });

  useEffect(() => {
    if (!runAfterSave) return;
    setState(prev => {
      if (prev.runTenantId === runAfterSave.tenantId && prev.runTenantName === runAfterSave.tenantName) {
        return prev;
      }
      return {
        ...prev,
        runTenantId: runAfterSave.tenantId,
        runTenantName: runAfterSave.tenantName,
      };
    });
  }, [runAfterSave, setState]);

  useEffect(() => {
    if (!state.samplePreview) {
      setAvailableColumns(undefined);
      setSampleRows([]);
      return;
    }
    setAvailableColumns(state.samplePreview.columns);
    setSampleRows(state.samplePreview.sampleRows);
  }, [state.samplePreview]);

  // Set of extra tab ids for quick membership checks in render / badge code.
  const extraTabIds = useMemo(
    () => new Set((extraTabs ?? []).map(t => t.id)),
    [extraTabs],
  );
  const isExtraTab = useCallback(
    (id: string) => extraTabIds.has(id),
    [extraTabIds],
  );

  // Precomputed tab badges. We push the values (as a stable object) into
  // EditorTabBar instead of a callback, so EditorTabBar's memo() can do a
  // meaningful shallow compare. On renders where no badge actually changed
  // (e.g. accepting an AI suggestion with no validation side-effect), the
  // useMemo returns the same object ref and the tab bar skips entirely.
  const tabBadges = useMemo(() => {
    const out: Record<string, TabBadge> = {};
    for (const t of visibleTabs) {
      if (isExtraTab(t.id)) {
        const errs = extraTabErrors?.(t.id, state) ?? [];
        out[t.id] = errs.length > 0
          ? 'error'
          : (state.visitedTabs.has(t.id) ? 'visited' : 'unvisited');
      } else {
        out[t.id] = getTabBadge(t.id as TemplateEditorTab, state);
      }
    }
    return out;
  }, [visibleTabs, state, isExtraTab, extraTabErrors]);
  const getBadge = useCallback(
    (tab: string) => tabBadges[tab] ?? 'unvisited',
    [tabBadges],
  );

  const getCurrentTabErrors = useCallback((): string[] => {
    if (isExtraTab(state.activeTab)) {
      return extraTabErrors?.(state.activeTab, state) ?? [];
    }

    const isBuiltIn = (id: string): id is TemplateEditorTab =>
      id === 'basics' || id === 'sampleData' || id === 'mappings' || id === 'matchKey'
      || id === 'geocoding' || id === 'review';

    return isBuiltIn(state.activeTab) ? tabErrors(state.activeTab) : [];
  }, [extraTabErrors, isExtraTab, state, tabErrors]);

  const getFirstExtraTabError = useCallback((): { tabId: string; message: string } | null => {
    for (const tab of visibleTabs) {
      if (!isExtraTab(tab.id)) continue;
      const errs = extraTabErrors?.(tab.id, state) ?? [];
      if (errs.length > 0) {
        return { tabId: tab.id, message: errs[0]! };
      }
    }
    return null;
  }, [extraTabErrors, isExtraTab, state, visibleTabs]);

  const ensurePersistedDraft = useCallback(async (
    patch: Pick<TemplateEditorState, 'mode' | 'entryGate' | 'runTenantId' | 'runTenantName'>,
  ): Promise<string | null> => {
    const existingId = autosaveIdRef.current ?? state._id;
    if (existingId) {
      const persistedId = existingId;
      setState(prev => ({
        ...prev,
        ...patch,
        _id: persistedId,
        dirty: false,
        isDraft: true,
        status: 'draft',
      }));
      return persistedId;
    }

    setCreatingDraft(true);
    setSaveError(null);
    try {
      const draftState: TemplateEditorState = {
        ...state,
        ...patch,
        dirty: false,
        isDraft: true,
        status: 'draft',
      };
      const payload = stateToTemplate(draftState, 'draft') as Partial<ImportTemplate> & {
        name: string;
      };
      payload.name = resolveDraftTemplateName(payload.name);
      const created = await services.createImportTemplate(payload);
      autosaveIdRef.current = created._id;
      setState(prev => ({
        ...prev,
        ...patch,
        _id: created._id,
        dirty: false,
        isDraft: true,
        status: 'draft',
      }));
      return created._id;
    } catch (err: any) {
      setSaveError(err?.message ?? 'Failed to start draft.');
      return null;
    } finally {
      setCreatingDraft(false);
    }
  }, [services, setState, state]);

  const handleEntryGateSelection = useCallback(async (
    gate: 'gate-1' | 'gate-2' | 'gate-3',
    nextRunTenantId?: string,
    nextRunTenantName?: string,
  ) => {
    const patch = resolveEntryGateSelection(gate, nextRunTenantId, nextRunTenantName);
    const savedId = await ensurePersistedDraft(patch);
    if (!savedId) return;
    if (gate === 'gate-3' && nextRunTenantId) {
      importEntryGate?.onChooseImport?.(nextRunTenantId);
      return;
    }
    if (patch.mode) {
      importEntryGate?.onChooseTemplateMode?.(patch.mode);
    }
  }, [ensurePersistedDraft, importEntryGate]);

  const handleNext = useCallback(() => {
    setSaveError(null);
    const didAdvance = goNext();

    if (didAdvance) {
      requestAnimationFrame(() => {
        panelScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return;
    }

    const errors = getCurrentTabErrors();
    if (errors.length > 0) {
      setSaveError(errors[0]);
    }

    requestAnimationFrame(() => {
      panelScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }, [getCurrentTabErrors, goNext]);

  // Stabilized footer label + onNext so EditorFooter's memo() can skip
  // re-render on unrelated state changes (e.g. AI accept click).
  const saveTemplateLabel = useMemo(() =>
    runAfterSave
      ? `Publish & import${runAfterSave.tenantName ? ` to ${runAfterSave.tenantName}` : ''}`
      : undefined,
    [runAfterSave],
  );
  const shouldUseRunPreviewPipeline = state.entryGate === 'gate-3';

  useEffect(() => {
    if (isNew || !templateId) return;
    setLoading(true);
    services
      .getImportTemplate(templateId)
      .then(t => {
        if (t) {
          setState(templateToState(t));
          autosaveIdRef.current = t._id;
        }
      })
      .catch(() => setSaveError('Failed to load template.'))
      .finally(() => setLoading(false));
  }, [isNew, templateId, setState, services]);

  const handleSaveDraft = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // Draft save: stateToTemplate('draft') emits status='draft',
      // isDraft=true, a draftResume blob (active tab / visited tabs /
      // authoring mode), and forces visibility='system-only' for
      // system-web origin so half-built drafts never fan out to
      // tenants. The user's chosen visibility is retained in state
      // and re-applied on publish.
      const payload = stateToTemplate(state, 'draft') as Partial<ImportTemplate> & {
        name: string;
      };
      const saveTarget = resolveTemplateSaveTarget(isNew, templateId, autosaveIdRef.current);
      if (saveTarget.mode === 'create') {
        const created = await services.createImportTemplate(payload);
        autosaveIdRef.current = created._id;
        setState(prev => ({
          ...prev,
          _id: created._id,
          dirty: false,
          isDraft: true,
          status: 'draft',
        }));
      } else if (saveTarget.templateId) {
        const targetId = saveTarget.templateId;
        await services.updateImportTemplate(targetId, payload);
        autosaveIdRef.current = targetId;
        setState(prev => ({
          ...prev,
          _id: targetId,
          dirty: false,
          isDraft: true,
          status: 'draft',
        }));
      }
    } catch (err: any) {
      setSaveError(err?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [state, isNew, templateId, setState, services]);

  const handleSaveTemplate = useCallback(async () => {
    if (!validateForSave()) return;
    const extraError = getFirstExtraTabError();
    if (extraError) {
      setSaveError(extraError.message);
      setState(prev => ({
        ...prev,
        activeTab: extraError.tabId,
        visitedTabs: new Set([...prev.visitedTabs, extraError.tabId]),
      }));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // Publish save: stateToTemplate('publish') emits status='published',
      // isDraft=false, draftResume cleared, and the user's chosen
      // visibility (no longer forced to system-only).
      const payload = stateToTemplate(state, 'publish') as Partial<ImportTemplate> & {
        name: string;
      };
      let savedId: string | undefined;
      const saveTarget = resolveTemplateSaveTarget(isNew, templateId, autosaveIdRef.current);
      if (saveTarget.mode === 'create') {
        const created = await services.createImportTemplate(payload);
        savedId = created._id;
        autosaveIdRef.current = created._id;
      } else if (saveTarget.templateId) {
        await services.updateImportTemplate(saveTarget.templateId, payload);
        savedId = saveTarget.templateId;
        autosaveIdRef.current = saveTarget.templateId;
      }
      setState(prev => ({
        ...prev,
        _id: savedId ?? prev._id,
        dirty: false,
        isDraft: false,
        status: 'published',
      }));
      // Card 3 follow-on: if the host parked a tenant for an import
      // run, hand off to `onSaveAndRun` instead of `onDone`. The saved
      // template id is the hinge — the host uses it to navigate into
      // the run wizard with the new template pre-wired. The captured
      // sample File (if any) is forwarded so the run flow does not
      // require a second upload.
      if (runAfterSave && onSaveAndRun && savedId) {
        onSaveAndRun(
          savedId,
          runAfterSave.tenantId,
          sampleFileRef.current ?? undefined,
          state.sampleBlob,
          state.samplePreview,
        );
      } else {
        onDone();
      }
    } catch (err: any) {
      setSaveError(err?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [
    getFirstExtraTabError,
    state,
    isNew,
    templateId,
    setState,
    services,
    validateForSave,
    onDone,
    runAfterSave,
    onSaveAndRun,
  ]);

  const confirmDiscardAndCancel = useCallback(() => {
    setDiscardModalOpen(false);
    onCancel();
  }, [onCancel]);

  const handleCancel = useCallback(() => {
    if (state.dirty) {
      setDiscardModalOpen(true);
      return;
    }
    onCancel();
  }, [state.dirty, onCancel]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-400">
        Loading template…
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col overflow-hidden">
        <div className="shrink-0 px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Back"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {isNew ? 'New Import Template' : `Edit: ${state.name || 'Untitled'}`}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                {state.dirty && (
                  <span className="text-[11px] text-amber-600">Unsaved changes</span>
                )}
                {state.isDraft && !isNew && (
                  <span className="text-[11px] text-gray-400">Draft</span>
                )}
              </div>
            </div>
          </div>
          {saveError && (
            <span className="text-xs text-red-600 max-w-xs truncate">{saveError}</span>
          )}
        </div>

        {state.mode === null ? (
          // Prefer the new 3-card ImportEntryGate when the host has
          // supplied config (system-web + tenant-web both do). Fall back
          // to the legacy 2-card TemplateModeGate so callers who haven't
          // migrated keep their current behavior — zero risk of a silent
          // regression during the rollout window.
          importEntryGate ? (
            <ImportEntryGate
              tenantOptions={importEntryGate.tenantOptions}
              tenantsLoading={importEntryGate.tenantsLoading}
              implicitTenantId={importEntryGate.implicitTenantId}
              implicitTenantName={importEntryGate.implicitTenantName}
              showImportCard={importEntryGate.showImportCard}
              onChooseTemplateMode={(mode) => {
                void handleEntryGateSelection(mode === 'shell' ? 'gate-1' : 'gate-2');
              }}
              // Card 3: transition into bound-template authoring
              // internally AND notify the host so it can park the tenant
              // (typically into URL state so a refresh persists it).
              onChooseImport={(tenantId) => {
                const tenantName = importEntryGate.implicitTenantId === tenantId
                  ? importEntryGate.implicitTenantName
                  : importEntryGate.tenantOptions?.find(t => t.id === tenantId)?.name;
                void handleEntryGateSelection('gate-3', tenantId, tenantName);
              }}
              onCancel={handleCancel}
            />
          ) : (
            <TemplateModeGate
              onChoose={(mode) => {
                void handleEntryGateSelection(mode === 'shell' ? 'gate-1' : 'gate-2');
              }}
              onCancel={handleCancel}
            />
          )
        ) : (
          <>
            <EditorTabBar
              tabs={visibleTabs}
              activeTab={state.activeTab}
              onTabClick={goToTab}
              getBadge={getBadge}
            />

            <div className="flex flex-1 min-h-0 overflow-hidden">
              <div
                ref={panelScrollRef}
                role="tabpanel"
                id={`tab-panel-${state.activeTab}`}
                aria-labelledby={`tab-${state.activeTab}`}
                className={[
                  'flex-1 min-h-0 px-6 py-6',
                  state.activeTab === 'sampleData'
                    ? 'flex flex-col overflow-hidden'
                    : 'overflow-y-auto',
                ].join(' ')}
              >
                {state.activeTab === 'basics' && (
                  <BasicsTabContent
                    state={state}
                    onChange={updateState}
                    errors={tabErrors('basics')}
                  />
                )}
                {state.activeTab === 'sampleData' && (
                  <div className="flex-1 min-h-0">
                    <SampleDataTabContent
                      state={state}
                      onChange={updateState}
                      errors={tabErrors('sampleData')}
                      availableColumns={availableColumns}
                      sampleRows={sampleRows}
                      restoredParseResult={state.samplePreview}
                      restoredSuggestions={state.aiSuggestions}
                      restoredDismissed={state.aiDismissed}
                      suppressInitialAutoSuggest={!!state.samplePreview}
                      onFileParsed={(cols, rows, meta) => {
                        setAvailableColumns(cols);
                        setSampleRows(rows);
                        updateState({
                          samplePreview: {
                            columns: cols,
                            sampleRows: rows,
                            totalRows: meta.totalRows,
                            fileType: meta.fileType,
                            activeSheet: meta.activeSheet,
                            availableSheets: meta.availableSheets,
                          },
                        });
                      }}
                      onFileSelected={(file) => {
                        sampleFileRef.current = file;
                      }}
                      parseFile={async (file: File, sampleSize, sheetName, onProgress) => {
                        if (!shouldUseRunPreviewPipeline) {
                          const p = await services.parseFileForTemplate(file, sampleSize ?? 100, sheetName, (pct) => {
                            onProgress?.({ kind: 'upload', pct });
                          });
                          return {
                            fileType: p.fileType,
                            columns: p.columns,
                            sampleRows: p.sampleRows,
                            totalRows: p.totalRows ?? p.sampleRows.length,
                            availableSheets: p.sheets,
                            activeSheet: p.activeSheet,
                          };
                        }
                        // Single upload path: blob upload → createPreviewJob →
                        // pollPreviewJob. Handles zipped shapefiles, multi-GB
                        // Excel, GeoPackage — everything the worker supports.
                        // There is no small-file fallback; the preview-job path
                        // is also fast for small files, and routing by size
                        // just re-introduces the bifurcation we're removing.
                        //
                        // We hand `onProgress` a translator so the Sample Data
                        // tab can render an informative status line: upload
                        // bytes while the file is streaming to Blob Storage,
                        // then worker job status ("queued" / "running") once
                        // it lands on the preview-jobs queue.
                        const uploaded = await services.uploadImportFile(file, (pct) => {
                          onProgress?.({ kind: 'upload', pct });
                        });
                        // Narrow `blobUrl` before handing it to the preview-job
                        // contract, which requires a non-null string. The upload
                        // service returns `blobUrl: string | null` because some
                        // host adapters might skip the blob step; for the unified
                        // pipeline this is always populated.
                        if (!uploaded.blobUrl) {
                          throw new Error('Upload did not return a blob URL — cannot start preview job.');
                        }
                        updateState({
                          sampleBlob: {
                            blobUrl: uploaded.blobUrl,
                            filename: file.name,
                            fileSize: file.size,
                            uploadedAt: new Date().toISOString(),
                          },
                        });
                        onProgress?.({ kind: 'job', status: 'queued' });
                        const { jobId } = await services.createPreviewJob({
                          blobUrl: uploaded.blobUrl,
                          filename: file.name,
                          fileSize: file.size,
                          sheetName,
                          sampleSize: sampleSize ?? 100,
                        });
                        onProgress?.({ kind: 'job', status: 'running' });
                        const p = await services.pollPreviewJob(jobId);
                        return {
                          fileType: p.fileType,
                          columns: p.columns,
                          sampleRows: p.sampleRows,
                          totalRows: p.totalRows ?? p.sampleRows.length,
                          availableSheets: p.sheets,
                          activeSheet: p.activeSheet,
                        };
                      }}
                      suggestMappings={async req =>
                        services.suggestMappings({
                          dataMode: req.dataMode,
                          columns: req.columns,
                          sampleRows: req.sampleRows,
                          tier: req.tier,
                        })
                      }
                    />
                  </div>
                )}
                {state.activeTab === 'mappings' && (
                  <RunWizardColumnMappingPanel
                    fieldMappings={state.fieldMappings}
                    onMappingsChange={(next) => updateState({ fieldMappings: next })}
                    dataMode={state.dataMode}
                    availableColumns={availableColumns ?? []}
                    sampleRows={sampleRows}
                    errors={tabErrors('mappings')}
                    suggestMappings={async req =>
                      services.suggestMappings({
                        dataMode: req.dataMode,
                        columns: req.columns,
                        sampleRows: req.sampleRows,
                        tier: req.tier,
                      })
                    }
                    interpretColumnGroups={services.interpretColumnGroups
                      ? async req => services.interpretColumnGroups!({
                          dataMode: req.dataMode,
                          columns: req.columns,
                          sampleRows: req.sampleRows,
                          groups: req.groups,
                          autoDetect: req.autoDetect,
                        })
                      : undefined
                    }
                  />
                )}
                {state.activeTab === 'matchKey' && (
                  <MatchKeyTabContent
                    state={state}
                    onChange={updateState}
                    errors={tabErrors('matchKey')}
                  />
                )}
                {state.activeTab === 'geocoding' && (
                  <GeocodingTabContent
                    state={state}
                    onChange={updateState}
                    errors={tabErrors('geocoding')}
                  />
                )}
                {state.activeTab === 'review' && (
                  <ReviewTabContent
                    state={state}
                    tabs={visibleTabs.filter(t => !isExtraTab(t.id))}
                    onGoToTab={goToTab}
                    onSaveDraft={handleSaveDraft}
                    onSaveTemplate={handleSaveTemplate}
                    saving={saving}
                    saveError={saveError}
                    saveTemplateLabel={
                      runAfterSave
                        ? `Publish & import${runAfterSave.tenantName ? ` to ${runAfterSave.tenantName}` : ''}`
                        : undefined
                    }
                  />
                )}
                {isExtraTab(state.activeTab) && renderExtraTab?.(
                  state.activeTab,
                  { state, updateState },
                )}
              </div>

              {/*
                The legacy FieldMappingDetailBlade (modal-based field editor)
                used to live here. It was paired with the old FieldMappings
                TabContent's per-row Edit button. RunWizardColumnMappingPanel
                edits every field inline (grouped catalog + combobox + ? /
                eye / X / T buttons per row), so the blade is no longer
                reachable from the authoring flow. Intentionally left out
                rather than conditionally rendered — any code path that tries
                to openBlade() on this wizard would be a bug.
              */}
            </div>

            <EditorFooter
              currentTabIndex={currentTabIndex}
              totalTabs={visibleTabs.length}
              onBack={goBack}
              onNext={handleNext}
              onSaveDraft={handleSaveDraft}
              onSaveTemplate={handleSaveTemplate}
              saving={saving || creatingDraft}
              isLastTab={isLastTab}
              saveTemplateLabel={saveTemplateLabel}
            />
          </>
        )}
      </div>

      <ConfirmDiscardModal
        open={discardModalOpen}
        onClose={() => setDiscardModalOpen(false)}
        onConfirm={confirmDiscardAndCancel}
      />
    </>
  );
}
